import React from "react";
import GenericForm, { FormFieldConfig } from "../../../../components/genericform/GenericForm";
import { useCustomerForm } from "../hooks/useCustomerForm";
import { sanitizePincode } from "../utils/validation";
import { useUser } from "src/business-types/grocery/components/UserContext";

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

// ✅ Extend FormFieldConfig to allow "section"
interface SectionFieldConfig extends Omit<FormFieldConfig, "id" | "onChange" | "value"> {
  type: "section";
  id: string;
  label: string;
}

type ExtendedFormFieldConfig = FormFieldConfig | SectionFieldConfig;

const CustomerForm: React.FC<CustomerFormProps> = ({ open, onClose, onSubmit }) => {
  const { formData, errors, updateField, handlePhoneChange, handleAlternativePhoneChange, validateForm, resetForm, isFormValid, handleInputChange } =
    useCustomerForm();
  const { user } = useUser()

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSubmit({
      Name: formData.name,
      phone_number: formData.phone,
      alternativenumber: formData.alternativePhone,
      Email: formData.email,
      Out_standing_balance: Number(formData.osb) ?? 0.0,
      Address: {
        street: formData.address,
        street2: formData.address2,
        street3: formData.address3,
      },
    });
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fields: ExtendedFormFieldConfig[] = [
    {
      id: "name",
      label: "Name",
      required: true,
      value: formData.name,
      // onChange: (val) => updateField("name", sanitizeName(val)),
      onChange: (val) => updateField("name", val),
    },
    {
      id: "phone",
      label: "Phone number",
      required: true,
      value: formData.phone,
      onChange: (val) => handlePhoneChange(val, formData.alternativePhone),
      error: errors.duplicatePhone,
      // helperText: errors.duplicatePhone ? "Alternate phone cannot be same as primary" : "",
    },
    {
      id: "alt-phone",
      label: "Alternative Phone number",
      value: formData.alternativePhone,
      onChange: (val) => handleAlternativePhoneChange(val, formData.phone),
      error: errors.duplicatePhone,
    },
    {
      id: "email",
      label: "Email",
      value: formData.email,
      onChange: (val) => updateField("email", val),
      error: errors.email,
      helperText: errors.email ? "Invalid email format" : "",
    },
    // ✅ Conditional field
    ...(user?.business_type !== "Restaurant"
      ? [
        {
          id: "osb",
          label: "Prev. out. Balance",
          value: formData.osb,
          onChange: (val: string) =>
            handleInputChange("osb", val.replace(/\D/g, "").slice(0, 6)),
        },
      ]
      : []),

    // ✅ Section heading inside GenericForm
    {
      id: "address-section",
      type: "section",
      label: "Address (Optional)",
    },
    {
      id: "street",
      label: "Street/Village",
      value: formData.address,
      onChange: (val) => updateField("address", val),
      variant: "standard",
    },
    {
      id: "district",
      label: "District/State",
      value: formData.address2,
      onChange: (val) => updateField("address2", val),
      variant: "standard",
    },
    {
      id: "pincode",
      label: "Pincode/Zipcode",
      value: formData.address3,
      onChange: (val) => updateField("address3", sanitizePincode(val)),
      variant: "standard",
    },
  ];

  return (
    <GenericForm
      open={open}
      title='Enter customer details'
      fields={fields}
      onClose={handleClose}
      onSubmit={handleSubmit}
      isFormValid={isFormValid}
      confirmLabel='Confirm'
    />
  );
};

export default CustomerForm;
